import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef, useMemo } from 'react'
import PropTypes from 'prop-types'

import SupplierCustomerAddressFormModal from '@/modules/base/components/generics/forms/address/address-form-modal'
import SelectInput from '@/modules/base/components/inputs/select-input/select-input'
import { GenericListsService } from '@/modules/base/components/generics/lists/genericListsService'
import { BASE_DATA_TYPES } from '../constants'
import { buildAndCreateSupplier } from '@/modules/supplier/supplierService'
import { buildAndCreateCustomer } from '@/modules/customer/customerService'
import { makeStyles, useTheme } from '@mui/styles'
import { toast } from '@/modules/base/components/toast/toastConfig'
import lang from '@/lang'
import { openNotesAlert } from '@/modules/_helpers/openNotesAlert'

/**
 *
 * @param {string} searchTerm
 * @param {Array} loadedOptions
 * @returns {Array}
 */
async function fetchSupplierAndCustomer(search, loadedOptions) {
  let filterModel
  if (search) {
    filterModel = {
      name: {
        filter: search,
        type: 'contains',
        filterType: 'text',
      },
      number: {
        filter: search,
        type: 'contains',
        filterType: 'text',
      },
      operator: 'OR',
    }
  }
  const startRow = loadedOptions?.length || 0

  const { data: customerAndSuppliers, meta } = await GenericListsService.getCustomerAndSuppliersSsr({ startRow, endRow: 10 + startRow, filterModel })
  const selectOptions = customerAndSuppliers.map(customerAndSupplier => {
    const { baseDataId, name, number, type } = customerAndSupplier
    const labelSuffix = type === BASE_DATA_TYPES.CUSTOMER ? '(K)' : '(L)'
    return {
      value: baseDataId,
      labelPrefix: number,
      labelSuffix,
      label: name,
      type: customerAndSupplier.type,
      data: customerAndSupplier,
    }
  })
  const hasMore = (loadedOptions?.length || 0 + customerAndSuppliers.length) !== meta.count
  return {
    options: selectOptions,
    hasMore,
  }
}

const useStyles = makeStyles((theme) => ({
  highlight: {
    borderColor: theme.colors.yellow,
    background: theme.yellowBackground,
  },
}))

const SupplierCustomerSelect = (props, ref) => {
  const {
    handleAdd,
    handleChange,
    handleCancel,
    inputValue,
    onInputChange,
    placeholder,
    autoFocus,
    loadingMessage,
    value,
    autoOpenMenu = true,
    isSavingDisabled,
    disableBackdropClick,
    customerSupplierFormData,
    showFillAnimation = false,
    onCreate,
    onModalClose,
    ...restProps
  } = props
  const [addressFormModalData, setAddressFormModalData] = useState({ companyName: '', open: false })
  const [addressFormModalOpen, setAddressFormModalOpen] = useState(false)
  const [menuIsOpen, setMenuIsOpen] = useState(!!inputValue && autoOpenMenu)
  const [defaultOptions, setDefaultOptions] = useState({ options: [] })
  const [forceRender, setForceRender] = useState(true)
  const selectInput = useRef()
  const theme = useTheme()
  const classes = useStyles()

  useEffect(() => {
    handleRefreshOptions()
  }, [])

  useEffect(() => {
    setMenuIsOpen(!!inputValue && autoOpenMenu)
  }, [inputValue])

  useImperativeHandle(ref, () => ({
    addNew: async(data) => {
      const { companyName } = data
      if (!companyName) { return }

      const { options } = await fetchSupplierAndCustomer(companyName)
      if (options.length <= 0) {
        setAddressFormModalData({ ...data })
        setAddressFormModalOpen(true)
      } else {
        handleSupplierCustomerChange(options[0])
      }
    },
    setValue: async(data, type) => {
      if (type === 'search') {
        const { options } = await fetchSupplierAndCustomer(data)
        if (options.length === 1) {
          return handleSupplierCustomerChange(options[0])
        }
        handleSupplierCustomerChange({ label: data, value: data, data: {} })
        return onInputChange && onInputChange(data)
      }
      handleSupplierCustomerChange({ label: data.name, labelPrefix: data.number, value: data.id, data, type })
    },
    resetModalData: () => {
      setAddressFormModalData({ companyName: '' })
    },
  }))

  const highlightStyles = showFillAnimation
    ? {
      control: (base) => ({
        ...base,
        borderColor: theme.colors.yellow,
        background: theme.yellowBackground,
      }),
    }
    : null

  const options = useMemo(() => ({
    infiniteScroll: true,
    allowCreate: true,
    handleChange: onInputChange,
    styles: highlightStyles,
    selectContainerClass: showFillAnimation && classes.highlight,
    customSelectOptions: {
      labelSuffixDelimiter: ' ',
      labelPrefixDelimiter: ' - ',
      isClearable: true,
      escapeClearsValue: true,
      hideSelectedOptions: false,
      placeholder,
      autoFocus,
      inputValue: inputValue?.replace(/\s+/g, ' '),
      defaultMenuIsOpen: false,
      defaultOptions: value?.value && defaultOptions.options.findIndex(o => o.value === value.value) === -1 ? { ...defaultOptions, options: [...defaultOptions.options, value] } : defaultOptions,
      loadOptions: fetchSupplierAndCustomer,
      loadingMessage,
      onInputChange,
      onMenuOpen: () => setMenuIsOpen(true),
      onMenuClose: () => setMenuIsOpen(false),
      menuIsOpen,
      createOptionPosition: 'first',
    },
  }), [inputValue, onInputChange, highlightStyles, showFillAnimation, placeholder, autoFocus, value, defaultOptions, loadingMessage, menuIsOpen])

  const handleRefreshOptions = () => {
    fetchSupplierAndCustomer('').then(setDefaultOptions)
  }

  const handleSupplierAdd = (name) => {
    onCreate && onCreate()
    setAddressFormModalOpen(true)
    setAddressFormModalData({ ...addressFormModalData, ...customerSupplierFormData, companyName: customerSupplierFormData?.companyName ?? name })
    selectInput?.current?.getSelectInput()?.blur && selectInput.current.getSelectInput().blur()
  }

  const handleAddressSubmit = (addresses) => {
    const [firstAddress] = addresses || []
    const type = firstAddress.addressKind === 'supplier' ? BASE_DATA_TYPES.SUPPLIER : BASE_DATA_TYPES.CUSTOMER

    if (isSavingDisabled) {
      handleAdd && handleAdd({ ...firstAddress, type })
    } else if (type === BASE_DATA_TYPES.SUPPLIER) {
      buildAndCreateSupplier(firstAddress, inputValue)
        .then((savedSupplier) => {
          handleSupplierCustomerChange({ label: savedSupplier.name, labelPrefix: savedSupplier.number, value: savedSupplier.id, type: BASE_DATA_TYPES.SUPPLIER, data: savedSupplier })
          handleRefreshOptions()
        }).catch((e) => {
          if (e?.body?.meta?.nameAlias) {
            const alias = JSON.parse(e.body.meta.message?.[0]?.code)?.supplier?.name
            toast.showToast({ type: 'error', message: lang.supplier.nameExistsAsAlias(alias) })
          } else {
            toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
          }
        })
    } else {
      buildAndCreateCustomer(firstAddress, inputValue)
        .then((savedCustomer) => {
          handleSupplierCustomerChange({ label: savedCustomer.name, labelPrefix: savedCustomer.number, value: savedCustomer.id, type: BASE_DATA_TYPES.CUSTOMER, data: savedCustomer })
          handleRefreshOptions()
        })
    }
  }

  const onCancel = () => {
    handleCancel && handleCancel()
  }

  const handleSupplierCustomerChange = (option) => {
    if (option?.data) {
      const { data: { notes, notesAlert } } = option

      if (notesAlert) {
        openNotesAlert(notes)
      }

      handleChange && handleChange(option)
    } else {
      handleChange && handleChange({})
    }
  }

  const addressFormModal = addressFormModalOpen
    ? (
      <SupplierCustomerAddressFormModal
        disableBackdropClick={disableBackdropClick}
        onSubmit={handleAddressSubmit}
        onCancel={() => {
          setAddressFormModalOpen(false)
          onCancel()
        }}
        data={customerSupplierFormData || addressFormModalData}
        onClose={() => {
          setAddressFormModalOpen(false)
          onModalClose && onModalClose()
          onModalClose && onCreate && setForceRender(!forceRender)
        }}
      />
    )
    : null

  return (
    <>
      <SelectInput
        key={forceRender}
        ref={selectInput}
        index={forceRender}
        {...restProps}
        {...options}
        onOptionCreated={handleSupplierAdd}
        handleChange={handleSupplierCustomerChange}
        value={value}
        fillAnimationOptions={{ show: showFillAnimation, color: theme.colors.yellow }}
      />
      {addressFormModal}
    </>
  )
}

SupplierCustomerSelect.propTypes = {
  handleAdd: PropTypes.func,
  handleChange: PropTypes.func,
  handleCancel: PropTypes.func,
  onInputChange: PropTypes.func,
  inputValue: PropTypes.string,
  placeholder: PropTypes.string,
  loadingMessage: PropTypes.string,
  autoFocus: PropTypes.bool,
  showFillAnimation: PropTypes.bool,
  isSavingDisabled: PropTypes.bool,
  value: PropTypes.object,
  autoOpenMenu: PropTypes.bool,
  customerSupplierFormData: PropTypes.object,
  onCreate: PropTypes.func,
  onModalClose: PropTypes.func,
  dataQsId: PropTypes.string,
}

export default forwardRef(SupplierCustomerSelect)
